/*
 * Lemonade Training Oy copy rights all right reserved.
 * Custom Claims Type Definition
 *
 * These types represent Firebase Auth custom claims structure
 */

import { AccessTier, ACCESS_TIER } from "./accessControl";
import { BoxMemberStatus, BoxRole } from "./box";

/* -------------- [ Custom Claims ] ------------------ */
export type CustomClaims = {
  // === Boolean flags (for Firebase security rules) ===
  athlete: boolean;
  boxCoach: boolean; // Indicates if user is a box-invited coach
  coach: boolean;
  /**
   * With a `boxID`: a legacy gym-admin flag, superseded by `boxRole` when that
   * is present. With `boxID: null`: a platform gym licence (Users.boxLicense) —
   * the holder may create a gym; `resolveBoxLicense` reads that shape. Never a
   * rules gate.
   */
  box: boolean;
  lemonadeAdmin: boolean;
  admin: boolean;

  // === Role identifiers ===
  /**
   * Legacy GLOBAL display-role label — capitalized values such as "Athlete",
   * "Coach", "Box Admin", "Lemonade Admin" (see the backend ROLE constants).
   * It describes what kind of account this is platform-wide; it is NOT
   * box-scoped and security rules do not gate on it. For "what may this user
   * do within a gym", use `boxRole`.
   */
  role: string;

  /**
   * Box-scoped RBAC role within the user's gym (`boxID`). Lowercase values
   * only — this is the claim firestore.rules and storage.rules gate on
   * (e.g. `request.auth.token.boxRole == 'admin'`), so its value set must
   * match what Cloud Functions actually write: "admin" | "coach" | "athlete",
   * or null when the user belongs to no box.
   *
   * Optional because tokens minted before box roles existed carry no
   * `boxRole` key at all; readers must treat `undefined` the same as null
   * (no box role). Distinct from the Firestore `Members/{uid}.role` field,
   * which is the server-side authoritative per-member record — the JWT claim
   * exists so rules can gate without a document read.
   */
  boxRole?: BoxRole | null;

  /**
   * Membership standing within `boxID`. firestore.rules and storage.rules
   * have gated on `request.auth.token.boxStatus` since before anything
   * declared or minted it; this declaration closes that gap.
   *
   * undefined = unknown, do not deny — tokens minted before this key existed
   * carry no value, and treating their absence as "not active" would strip
   * access from every staff member still on an old token. Only a KNOWN
   * non-active value denies.
   */
  boxStatus?: BoxMemberStatus | null;

  // === Access levels ===
  accessLevel: number; // LEGACY: Keep for mobile app backward compatibility (0, 2, 3, 6, 8, 10)
  accessTier: AccessTier; // NEW: Hierarchical access tier (100-gap values)

  // === Scope ===
  coachType: "independent" | "box" | null; // Coach type
  boxID: string | null; // Box ID if coachType === "box"
};

/* -------------- [ Custom Claims Initial Values ] ------------------ */
export const CUSTOM_CLAIMS_INITIAL: CustomClaims = {
  athlete: true,
  boxCoach: false,
  coach: false,
  box: false,
  lemonadeAdmin: false,
  admin: false,
  role: "Athlete",
  boxRole: null,
  accessLevel: 0, // LEGACY
  accessTier: ACCESS_TIER.ATHLETE, // NEW
  coachType: null,
  boxID: null,
};

/* -------------- [ Coach Type Constants ] ------------------ */
export const COACH_TYPE = {
  INDEPENDENT: "independent",
  BOX: "box",
} as const;

export type CoachTypeConstant = (typeof COACH_TYPE)[keyof typeof COACH_TYPE];
